<!-- Energy widget version: 1.0 -->
<script lang="ts">
	import { devices, user_settings } from 'src/stores';
	import {
		consumption_by_device_account,
		consumption_device,
		consumption_rooms,
	} from 'src/stores/features/consumption';
	import { rooms } from 'src/stores';
	import EmKpiCard from 'src/components/EnergyMonitoring/sections/EmKpiCard.svelte';
	import { buildConsumptionStores } from 'src/components/EnergyMonitoring/helpers/consumption_helpers';
	import {
		buildEnergyKpis,
		type EnergyKpiCard,
	} from 'src/components/EnergyMonitoring/helpers/energy_kpi_helpers';
	import { DEMO_KPI_CARDS } from 'src/components/EnergyMonitoring/helpers/energy_demo_data';
	import {
		aggregateTariffBuckets,
		buildTariffSummary,
	} from 'src/components/EnergyMonitoring/helpers/energy_tariff_helpers';
	import { fetchEnergyStatistics } from 'src/components/EnergyMonitoring/helpers/fetch_energy_statistics';
	import { getControlsForEnergyConfig } from '../../../../dash-energy-period';
	import { normalizeEnergyConfig, normalizeInfoCategories } from '../../../../dash-energy-helpers';
	import type { DashEnergyInfoKpiId, DashEnergyWidgetConfig } from '../../../../dash-energy-types';
	import type { ChartStatisticInterface } from 'src/types/Chart';
	import type { TariffSummary } from 'src/components/EnergyMonitoring/helpers/energy_tariff_helpers';
	import { energyMonitoringDataMode } from 'src/components/EnergyMonitoring/helpers/store';
	import { buildDemoEnergyMonitoringStats } from 'src/components/EnergyMonitoring/helpers/energy_demo_providers';
	import EmWidgetState from 'src/components/EnergyMonitoring/EmWidgetState.svelte';
	import { format } from 'svelte-i18n';
	import { currentUnitId } from 'src/stores/account/home';

	export let energyConfig: DashEnergyWidgetConfig<'dashEnergyInfo'> | undefined;
	export let staticPreview = false;
	export let fixedColumns: number | undefined;

	$: config = normalizeEnergyConfig('dashEnergyInfo', energyConfig);
	$: categories = normalizeInfoCategories(config.infoCategories);

	const PERIOD_CATEGORY_IDS: DashEnergyInfoKpiId[] = [
		'periodConsumption',
		'periodGeneration',
		'tariff',
		'offPeak',
		'peakDemand',
	];
	let statistic: ChartStatisticInterface | null = null;
	let tariffSummary: TariffSummary | null = null;
	let loadError = false;
	let loadToken = 0;

	const loadPeriodData = async (
		config: DashEnergyWidgetConfig<'dashEnergyInfo'>,
		categories: DashEnergyInfoKpiId[],
		staticPreview: boolean,
		unitId: string,
		dataMode: 'real' | 'demo',
	) => {
		void unitId;
		const token = ++loadToken;
		if (!categories.some((id) => PERIOD_CATEGORY_IDS.includes(id))) {
			statistic = null;
			tariffSummary = null;
			loadError = false;
			return;
		}
		if (staticPreview || dataMode === 'demo') {
			const demo = buildDemoEnergyMonitoringStats();
			statistic = demo.statistic;
			tariffSummary = buildTariffSummary(demo.tariffBuckets);
			loadError = false;
			return;
		}

		loadError = false;
		try {
			const result = await fetchEnergyStatistics(
				getControlsForEnergyConfig(config),
				config.period,
			);
			if (token !== loadToken) return;
			statistic = result?.statistic ?? null;
			tariffSummary = buildTariffSummary(aggregateTariffBuckets(result?.history ?? []));
			loadError = false;
		} catch {
			if (token !== loadToken) return;
			statistic = null;
			tariffSummary = null;
			loadError = true;
		}
	};

	const refreshPeriodData = () =>
		loadPeriodData(config, categories, staticPreview, $currentUnitId, $energyMonitoringDataMode);

	$: loadPeriodData(config, categories, staticPreview, $currentUnitId, $energyMonitoringDataMode);

	$: consumptionStores = buildConsumptionStores({
		rooms: $rooms,
		devices: $devices,
		consumptionRooms: $consumption_rooms,
		consumptionDevice: $consumption_device,
		consumptionByDeviceAccount: $consumption_by_device_account,
	});

	$: liveCards = buildEnergyKpis(
		consumptionStores,
		$devices,
		$user_settings?.currency ?? '',
		statistic,
		config.period,
		tariffSummary,
		staticPreview || $energyMonitoringDataMode === 'demo',
	).filter((entry) => categories.includes(entry.id as DashEnergyInfoKpiId));

	let previewCards: EnergyKpiCard[] | undefined;
	let previewUnitId: string | undefined;
	$: if ($currentUnitId !== previewUnitId) {
		previewUnitId = $currentUnitId;
		previewCards = undefined;
	}
	$: cards = staticPreview
		? (previewCards ??
			(previewCards = categories.map(
				(id) =>
					liveCards.find((entry) => entry.id === id) ??
					DEMO_KPI_CARDS.find((entry) => entry.id === id)!,
			)))
		: liveCards;
</script>

{#if cards.length}
	<div class="dash-energy-info-container">
		{#if loadError}
			<EmWidgetState
				kind="error"
				compact
				message={$format('energy_monitoring_load_error', {
					default: 'Some energy totals could not be loaded.',
				})}
				onRefresh={refreshPeriodData}
			/>
		{/if}
		<div
			class="dash-energy-info-widget"
			class:fixed-columns={fixedColumns !== undefined}
			style={`--info-count: ${fixedColumns ?? cards.length}`}
		>
			{#each cards as card (card.id)}
				<EmKpiCard {card} />
			{/each}
		</div>
	</div>
{/if}

<style lang="scss">
	.dash-energy-info-container {
		container-type: inline-size;
		container-name: energy-info;
		width: 100%;
		min-width: 0;
	}

	.dash-energy-info-widget {
		display: grid;
		grid-template-columns: repeat(var(--info-count, 1), minmax(0, 1fr));
		grid-auto-rows: auto;
		align-items: stretch;
		gap: var(--spacing-s);
		min-width: 0;
		width: 100%;
	}

	@container energy-info (max-width: 640px) {
		.dash-energy-info-widget:not(.fixed-columns) {
			grid-template-columns: repeat(2, minmax(0, 1fr));
		}
	}

	@container energy-info (max-width: 280px) {
		.dash-energy-info-widget:not(.fixed-columns) {
			grid-template-columns: minmax(0, 1fr);
		}
	}
</style>
